/**
 * ARCHITECTURE: Unified Login (Task 47 Phase 3)
 *
 * Single entry point for both customers (magic-link) and admins/partners
 * (password + MFA). Three top-level modes:
 *
 *   1. 'email'           — email-only form. Submits to POST /api/auth/email-check
 *                          which returns { method: 'magic_link' | 'password_mfa' }.
 *   2. 'magic_link_sent' — magic link + 6-digit code path. Reuses CodeEntry.
 *   3. 'password_mfa'    — admin/partner path. Sub-flow: password → mfaVerify →
 *                          (mfaSetup → mfaConfirm) when first-time MFA setup needed.
 *
 * Returning the same `magic_link` response for unknown emails is deliberate
 * (Open Question 4) — the user gets the magic-link UX even if their email
 * isn't recognised, which prevents this endpoint from leaking who has an
 * admin account. The downstream sendMagicLink call to a non-existent email
 * is rate-limited and returns a generic success.
 *
 * Magic-link URL token (e.g. ?token=abc) is still auto-validated on mount,
 * matching v1 behaviour — the email link itself remains a one-click path.
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { apiService, partnerApiService } from '../utils/api';
import CodeEntry from '../components/CodeEntry';
import logoWhite from '../assets/logo-repro-white.svg';
import logoDark from '../assets/logo-repro-v3-dark.svg';

type Mode = 'email' | 'magic_link_sent' | 'password_mfa';
type MfaStep = 'password' | 'verify' | 'setup' | 'confirm';

const Login = () => {
  const [mode, setMode] = useState<Mode>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Magic-link path state
  const [codeError, setCodeError] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState<
    number | undefined
  >();

  // Password+MFA path state
  const [mfaStep, setMfaStep] = useState<MfaStep>('password');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrCode, setQrCode] = useState('');

  const { login, setOrganizations } = useAuth();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();

  const logo = resolvedTheme === 'dark' ? logoWhite : logoDark;

  // Arriving at /login means the user wants to (re-)authenticate. Clear the
  // partner localStorage flag so a stale value doesn't confuse the App.tsx
  // route gate. Customer auth (in useAuth) is left alone — a valid session
  // can keep working alongside the login form.
  useEffect(() => {
    localStorage.removeItem('partner_authenticated');
  }, []);

  // Auto-validate magic link token from URL (clicking link in email still works)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (!token) return;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await apiService.validateToken(token);
        if (result.success && result.email && result.organizations) {
          login(result.email, token);
          setOrganizations(result.organizations);
        } else {
          setError('Invalid or expired link. Please request a new one.');
        }
      } catch {
        setError('An error occurred while validating the link.');
      } finally {
        setLoading(false);
      }
    })();
  }, [login, setOrganizations]);

  // ---------------------------------------------------------------------------
  // Step 1: email submit → branch on backend response
  // ---------------------------------------------------------------------------

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { method } = await apiService.checkAuthMethod(email);

      if (method === 'password_mfa') {
        setMode('password_mfa');
        setMfaStep('password');
        return;
      }

      // method === 'magic_link' — send the link, transition to code entry
      const result = await apiService.sendMagicLink(email);
      if (result.success) {
        setMode('magic_link_sent');
        setCodeError('');
        setAttemptsRemaining(undefined);
      } else {
        setError(result.message || 'Failed to send verification code.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Magic-link path
  // ---------------------------------------------------------------------------

  const handleCodeSubmit = async (code: string) => {
    setCodeError('');
    const result = await apiService.validateCode(email, code);

    if (result.success && result.email && result.organizations) {
      login(result.email, 'code-auth');
      setOrganizations(result.organizations);
    } else {
      setAttemptsRemaining(result.attemptsRemaining);
      setCodeError(result.message || 'Invalid verification code.');
    }
  };

  const handleResend = async () => {
    setCodeError('');
    setAttemptsRemaining(undefined);
    const result = await apiService.sendMagicLink(email);
    if (!result.success) {
      setCodeError(result.message || 'Failed to resend code.');
    }
  };

  // ---------------------------------------------------------------------------
  // Password + MFA path
  // ---------------------------------------------------------------------------

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await partnerApiService.login(email, password);
      if (!result.success) {
        setError(result.error || 'Login failed');
        return;
      }

      const data = result.data;
      if (data.mfaRequired && data.mfaToken) {
        setMfaToken(data.mfaToken);
        setMfaStep('verify');
      } else if (data.mfaSetupRequired) {
        setMfaStep('setup');
      } else {
        // Should not happen — admin auth requires MFA
        setError('Unexpected auth state.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await partnerApiService.verifyMfa(mfaToken, totpCode);
      if (result.success) {
        // Partner / admin auth uses the cookie set by the backend; the
        // App.tsx route gate also accepts this localStorage flag.
        localStorage.setItem('partner_authenticated', 'true');
        navigate('/dashboard');
      } else {
        setError(result.error || 'MFA verification failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA verification failed');
    } finally {
      setLoading(false);
      setTotpCode('');
    }
  };

  const handleMfaSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await partnerApiService.setupMfa();
      if (result.success) {
        setQrCode(result.data.qrCodeDataUrl);
        setMfaStep('confirm');
      } else {
        setError(result.error || 'MFA setup failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA setup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await partnerApiService.confirmMfa(totpCode);
      if (result.success) {
        localStorage.setItem('partner_authenticated', 'true');
        navigate('/dashboard');
      } else {
        setError(result.error || 'MFA confirmation failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA confirmation failed');
    } finally {
      setLoading(false);
      setTotpCode('');
    }
  };

  const handleBackToEmail = () => {
    setMode('email');
    setError('');
    setCodeError('');
    setAttemptsRemaining(undefined);
    setPassword('');
    setMfaToken('');
    setTotpCode('');
    setQrCode('');
    setMfaStep('password');
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const headerCopy =
    mode === 'email'
      ? "We'll send a magic link to customer accounts, or prompt for password + MFA on partner accounts."
      : mode === 'magic_link_sent'
        ? 'Enter the verification code we sent to your email.'
        : mfaStep === 'password'
          ? 'Sign in with your password.'
          : mfaStep === 'verify'
            ? 'Enter your authenticator code.'
            : mfaStep === 'setup'
              ? 'Set up two-factor authentication to continue.'
              : 'Scan the QR code and confirm with your authenticator.';

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-200 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src={logo} alt="RePro" className="mx-auto mb-4 h-8" />
          <h1 className="text-xl font-semibold text-base-content">Sign in</h1>
          <p className="mt-1 text-sm text-base-content/60">{headerCopy}</p>
        </div>

        {mode === 'email' && (
          <form
            onSubmit={handleEmailSubmit}
            className="card bg-base-100 shadow-md"
          >
            <div className="card-body gap-4 text-left">
              {error && (
                <div className="alert alert-error text-sm">{error}</div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block pb-1 pl-1 text-sm text-base-content"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input input-bordered w-full"
                  placeholder="you@company.com"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className="btn btn-primary mt-2 w-full"
              >
                {loading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </form>
        )}

        {mode === 'magic_link_sent' && (
          <CodeEntry
            email={email}
            onSubmit={handleCodeSubmit}
            onResend={handleResend}
            onBack={handleBackToEmail}
            error={codeError}
            attemptsRemaining={attemptsRemaining}
          />
        )}

        {mode === 'password_mfa' && (
          <div className="card bg-base-100 shadow-md">
            <div className="card-body gap-4 text-left">
              {error && (
                <div className="alert alert-error text-sm" role="alert">
                  {error}
                </div>
              )}

              {mfaStep === 'password' && (
                <form
                  onSubmit={handlePasswordSubmit}
                  className="flex flex-col gap-4"
                >
                  <p className="text-sm text-base-content/70">
                    Signing in as <span className="font-medium">{email}</span>
                  </p>
                  <div>
                    <label
                      htmlFor="password"
                      className="block pb-1 pl-1 text-sm text-base-content"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      required
                      autoComplete="current-password"
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input input-bordered w-full"
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !password}
                    className="btn btn-primary w-full"
                  >
                    {loading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      'Sign in'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="btn btn-ghost btn-sm w-full"
                  >
                    Back
                  </button>
                </form>
              )}

              {mfaStep === 'verify' && (
                <form
                  onSubmit={handleMfaVerify}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label
                      htmlFor="totp-verify"
                      className="block pb-1 pl-1 text-sm text-base-content"
                    >
                      Authenticator code
                    </label>
                    <input
                      id="totp-verify"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      value={totpCode}
                      onChange={(e) =>
                        setTotpCode(
                          e.target.value.replace(/\D/g, '').slice(0, 6),
                        )
                      }
                      className="input input-bordered w-full text-center text-2xl tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || totpCode.length !== 6}
                    className="btn btn-primary w-full"
                  >
                    {loading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      'Verify'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="btn btn-ghost btn-sm w-full"
                  >
                    Back
                  </button>
                </form>
              )}

              {mfaStep === 'setup' && (
                <div className="flex flex-col gap-4 text-center">
                  <p className="text-sm text-base-content/70">
                    You need to set up two-factor authentication before signing
                    in.
                  </p>
                  <button
                    type="button"
                    onClick={handleMfaSetup}
                    disabled={loading}
                    className="btn btn-primary w-full"
                  >
                    {loading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      'Set up 2FA'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="btn btn-ghost btn-sm w-full"
                  >
                    Back
                  </button>
                </div>
              )}

              {mfaStep === 'confirm' && (
                <form
                  onSubmit={handleMfaConfirm}
                  className="flex flex-col gap-4"
                >
                  {qrCode && (
                    <div className="flex justify-center">
                      <img
                        src={qrCode}
                        alt="QR code for authenticator app"
                        className="h-48 w-48"
                      />
                    </div>
                  )}
                  <p className="text-center text-sm text-base-content/70">
                    Scan the QR code with your authenticator app, then enter the
                    6-digit code.
                  </p>
                  <div>
                    <label htmlFor="totp-confirm" className="sr-only">
                      Authenticator code
                    </label>
                    <input
                      id="totp-confirm"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      value={totpCode}
                      onChange={(e) =>
                        setTotpCode(
                          e.target.value.replace(/\D/g, '').slice(0, 6),
                        )
                      }
                      className="input input-bordered w-full text-center text-2xl tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || totpCode.length !== 6}
                    className="btn btn-primary w-full"
                  >
                    {loading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      'Confirm & activate'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
